import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';

if (typeof window !== 'undefined' && !window.__schemModalCount) {
  window.__schemModalCount = 0;
}

function postToParent(payload) {
  try { window.parent?.postMessage(payload, '*'); } catch {}
}

const Modal = ({ isOpen, onClose, heading, children, mobileFullscreen = false, bodyClassName }) => {
  const dialogRef = useRef(null);
  const scrollYRef = useRef(0);
  const activeRef = useRef(false);
  const openedAtRef = useRef(0);

  // Lock body scroll inside the iframe
  useEffect(() => {
    if (!isOpen) return;

    scrollYRef.current = window.scrollY || 0;
    const prev = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
    };
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollYRef.current}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';

    // focus modal without scrolling the page
    requestAnimationFrame(() => {
      dialogRef.current?.focus?.({ preventScroll: true });
    });

    return () => {
      Object.assign(document.body.style, prev);
      window.scrollTo(0, scrollYRef.current);
    };
  }, [isOpen]);

  useEffect(() => {
    // OPEN edge
    if (isOpen && !activeRef.current) {
      activeRef.current = true;
      window.__schemModalCount += 1;
      openedAtRef.current = Date.now();   // <-- mark open time
      if (window.__schemModalCount === 1) {
        postToParent({ action: 'schematicsModalOpen', data: { open: true, viewportHeight: window.innerHeight } });
      }
    }

    // CLOSE edge
    if (!isOpen && activeRef.current) {
      activeRef.current = false;
      window.__schemModalCount = Math.max(0, window.__schemModalCount - 1);
      if (window.__schemModalCount === 0) {
        postToParent({ action: 'schematicsModalOpen', data: { open: false } });
      }
    }
  }, [isOpen]);

  // safety: if this Modal unmounts while "active", decrement once
  useEffect(() => {
    return () => {
      if (activeRef.current) {
        activeRef.current = false;
        window.__schemModalCount = Math.max(0, window.__schemModalCount - 1);
        console.log('Modal unmounted, stack--:', window.__schemModalCount);
        if (window.__schemModalCount === 0) {
          postToParent({ action: 'schematicsModalOpen', data: { open: false } });
        }
      }
    };
  }, []);

  if (!isOpen) return null;

  // Backdrop handlers with grace period (~200ms) to avoid “open then immediately close”
  const backdropAttemptClose = () => {
    if (Date.now() - openedAtRef.current < 220) return; // ignore early ghost click
    onClose();
  };

  const overlay = (
    <div
      className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-0 md:px-4 md:py-10 overflow-auto"
      role="dialog"
      aria-modal="true"
      aria-label={heading || 'Dialog'}
      // use mousedown/touchstart to beat ghost clicks
      onMouseDown={backdropAttemptClose}
      onTouchStart={backdropAttemptClose}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={
          "relative bg-white shadow-lg flex flex-col outline-none " +
          (mobileFullscreen
            // MOBILE: truly fullscreen; DESKTOP: roomy with capped height
            ? "w-screen h-[100dvh] rounded-none md:w-[90%] md:max-w-6xl md:h-auto md:max-h-[calc(100vh-5rem)] md:rounded-lg"
            : "w-[90%] md:w-full max-w-6xl md:max-h-[calc(100vh-5rem)] md:rounded-lg")
        }
        // stop events so the backdrop doesn't see them
        onMouseDown={(e) => { console.log('[modal] panel mousedown'); e.stopPropagation(); }}
        onTouchStart={(e) => { console.log('[modal] panel touchstart'); e.stopPropagation(); }}
      >
        <div className="sticky top-0 z-50 bg-[#072743] flex justify-between items-center px-[20px] py-[15px] flex-shrink-0">
          <h2 className="text-xl font-bold text-white">{heading}</h2>
          <button type="button" onClick={onClose} className="text-white">
            <X size={24} color="white" />
          </button>
        </div>

        <div className={bodyClassName ?? "px-[20px] py-[15px] overflow-y-auto overscroll-contain flex-1"}>
          {children}
        </div>
      </div>
    </div>
  );

  // Render above any local stacking contexts
  return createPortal(overlay, document.body);
};

export default Modal;
